import { useEffect, useRef, useState } from 'react';
import { useInView, useReducedMotion } from 'framer-motion';

/**
 * Count a figure up once it is on screen.
 *
 * The value is written the way the design writes it — "20,000" — so the
 * separators and any suffix are taken from that string rather than reinvented,
 * and only the digits are animated.
 */
export function Counter({ value, className }: { value: string; className?: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const still = useReducedMotion();
  const target = Number(value.replace(/[^\d]/g, '')) || 0;
  const [shown, setShown] = useState(still ? target : 0);

  useEffect(() => {
    if (!inView || still) return;
    const DURATION = 1600;
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / DURATION);
      // Ease out so the last digits settle rather than snap.
      setShown(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, still, target]);

  // Re-apply the design's own formatting to the running number.
  const formatted = value.replace(/[\d,. ]*\d/, shown.toLocaleString('en-US'));

  return (
    <p ref={ref} className={className}>
      {formatted}
    </p>
  );
}
